/**
 * useEntitlements (ADR 042, WEB-7; default-deny — Phase 7)
 *
 * Fetches the per-workspace entitlement projection and exposes selectors over
 * it. The map is a RENDER HINT — the API middleware (and the authoritative
 * `GET /modules` filter) is the real gate. Under default-deny (ADR §13) a
 * catalogued feature the loaded projection does NOT grant resolves to `hidden`.
 * While the map is still LOADING we default to `granted` so a granted feature
 * never flash-hides (WEB-4); a key with no catalogued feature is un-gated and
 * also `granted`.
 */
import { useCallback } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useSession } from '@/features/auth/hooks/useSession'
import { resolveWorkspaceId } from '@/lib/workspace-routing'
import { queryKeys } from '@/lib/query/keys'
import {
  getWorkspaceEntitlements,
  type EntitlementMap,
  type EntitlementVerdict,
} from '@/lib/api/endpoints/entitlements'
import { featureKeyForModuleType } from '../lib/feature-module-map'

/**
 * Query the entitlement map for a workspace. When `explicitWorkspaceId` is
 * omitted it resolves the active workspace from the session. Disabled (and
 * therefore returns no data) until both a token and a workspace id are known.
 */
export function useEntitlements(explicitWorkspaceId?: string | null) {
  const { token, selectedContainer } = useSession()
  const workspaceId = explicitWorkspaceId ?? resolveWorkspaceId(selectedContainer)

  return useQuery<EntitlementMap>({
    queryKey: queryKeys.entitlements.map(workspaceId ?? ''),
    queryFn: () => getWorkspaceEntitlements(token as string, workspaceId as string),
    enabled: Boolean(token) && Boolean(workspaceId),
    staleTime: 60_000,
  })
}

/**
 * Selector for a single feature's verdict (DEFAULT-DENY — Phase 7).
 * `feature === undefined` (an un-gated surface) resolves to `granted`. While the
 * map is still loading we return `granted` so a granted feature never
 * flash-hides (WEB-4). Once the map has LOADED, a key missing from it resolves
 * to `hidden` (default-deny: a feature the projection didn't grant is concealed).
 */
export function useEntitlement(
  feature: string | undefined,
  workspaceId?: string | null,
): { verdict: EntitlementVerdict; isLoading: boolean } {
  const { data, isLoading } = useEntitlements(workspaceId)
  let verdict: EntitlementVerdict
  if (!feature) verdict = 'granted'
  else if (!data) verdict = 'granted'
  else verdict = data[feature] ?? 'hidden'
  return { verdict, isLoading }
}

/**
 * A stable predicate `(moduleType) => boolean` that reports whether a module
 * type is entitlement-concealed under DEFAULT-DENY (ADR §13, Phase 7). Used to
 * filter nav/rail launch affordances (WEB-8) so an ungranted module type has no
 * orphaned launch entry — the `GET /modules` filter already drops ungranted
 * module INSTANCES, but the rail also offers affordances for granted-but-not-yet-
 * created types, and those must follow the same gate.
 *
 * Un-catalogued types (no feature key — e.g. `sidebar_settings`) are never
 * hidden. While the map is still loading we return `false` (never hide) so a
 * granted module never flash-hides; once loaded, a type whose verdict is not
 * `granted` (missing key or an explicit non-`granted` verdict) is hidden.
 */
export function useIsModuleTypeHidden(
  workspaceId?: string | null,
): (moduleType: string) => boolean {
  const { data } = useEntitlements(workspaceId)
  return useCallback(
    (moduleType: string) => {
      if (!data) return false // still loading → never hide (no flash)
      const featureKey = featureKeyForModuleType(moduleType)
      if (!featureKey) return false // un-catalogued → render unconditionally
      return data[featureKey] !== 'granted' // default-deny
    },
    [data],
  )
}
